import {
  createBlankDraft,
  createEducation,
  createExperience,
  createProject,
  createSkill,
  validateDraftFormats,
  type ResumeDraft,
} from "./model.ts";

export const RESUME_IMPORT_FORMAT = "zeitpoint.resume-import";
export const RESUME_IMPORT_VERSION = 1;
export const MAX_RESUME_IMPORT_BYTES = 512 * 1024;

export interface ResumeImportSummary {
  name: string;
  locale: string;
  experience: number;
  education: number;
  skills: number;
  projects: number;
}

export interface ResumeImportResult {
  draft: ResumeDraft;
  summary: ResumeImportSummary;
  warnings: string[];
}

export class ResumeImportError extends Error {
  readonly issues: string[];

  constructor(issues: string[]) {
    super(issues[0] ?? "The JSON does not match the Zeitpoint import format.");
    this.name = "ResumeImportError";
    this.issues = issues;
  }
}

export const resumeImportTemplate = {
  $schema: "urn:balcore:bep:schema:zeitpoint-resume-import:v1",
  format: RESUME_IMPORT_FORMAT,
  version: RESUME_IMPORT_VERSION,
  locale: "en",
  resume: {
    basics: {
      fullName: "",
      professionalHeadline: "",
      professionalSummary: "",
    },
    contact: {
      email: "",
      phone: "",
      cityRegion: "",
      website: "",
    },
    experience: [
      {
        organization: "",
        role: "",
        location: "",
        startDate: "",
        endDate: "",
        current: false,
        summary: "",
        achievements: [""],
      },
    ],
    education: [
      {
        institution: "",
        qualification: "",
        fieldOfStudy: "",
        location: "",
        startDate: "",
        endDate: "",
        current: false,
        details: "",
      },
    ],
    skills: [
      {
        name: "",
        level: "",
        keywords: [""],
      },
    ],
    projects: [
      {
        name: "",
        description: "",
        url: "",
        startDate: "",
        endDate: "",
        outcomes: [""],
      },
    ],
  },
} as const;

export function resumeImportTemplateJSON(): string {
  return `${JSON.stringify(resumeImportTemplate, null, 2)}\n`;
}

export function resumeImportAIInstructions(): string {
  return [
    "Convert the resume information I provide into the exact Zeitpoint JSON template below.",
    "Return JSON only. Do not use Markdown fences or add explanatory text.",
    "Do not invent employers, dates, qualifications, links, achievements, or skills.",
    "Keep unknown scalar values as empty strings and unknown collections as empty arrays.",
    "Use YYYY, YYYY-MM, or YYYY-MM-DD for dates. If current is true, endDate must be empty.",
    "Keep format, version, property names, and nesting exactly as shown.",
    "Preserve the language of the supplied resume and set locale accordingly.",
    "",
    resumeImportTemplateJSON().trimEnd(),
  ].join("\n");
}

export function parseResumeImportJSON(source: string): ResumeImportResult {
  if (new Blob([source]).size > MAX_RESUME_IMPORT_BYTES) {
    throw new ResumeImportError(["The JSON file is larger than 512 KB. Remove unrelated or embedded content and try again."]);
  }

  let value: unknown;
  try {
    value = JSON.parse(source);
  } catch (error) {
    const detail = error instanceof SyntaxError ? error.message : "JSON parsing failed.";
    throw new ResumeImportError([`The file is not valid JSON. ${detail}`]);
  }

  const issues: string[] = [];
  const envelope = objectAt(value, "$", issues);
  rejectUnknownKeys(envelope, ["$schema", "format", "version", "locale", "resume"], "$", issues);
  if (envelope.format !== RESUME_IMPORT_FORMAT) {
    issues.push(`$.format must be "${RESUME_IMPORT_FORMAT}".`);
  }
  if (envelope.version !== RESUME_IMPORT_VERSION) {
    issues.push(`$.version must be ${RESUME_IMPORT_VERSION}.`);
  }

  const locale = optionalString(envelope, "locale", "$", 35, issues) || "en";
  if (!/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(locale)) {
    issues.push("$.locale must be a language tag such as en, de, or en-US.");
  }

  const resume = objectAt(envelope.resume, "$.resume", issues);
  rejectUnknownKeys(resume, ["basics", "contact", "experience", "education", "skills", "projects"], "$.resume", issues);
  const basics = optionalObject(resume, "basics", "$.resume", issues);
  const contact = optionalObject(resume, "contact", "$.resume", issues);
  rejectUnknownKeys(basics, ["fullName", "professionalHeadline", "professionalSummary"], "$.resume.basics", issues);
  rejectUnknownKeys(contact, ["email", "phone", "cityRegion", "website"], "$.resume.contact", issues);

  const draft = createBlankDraft();
  draft.locale = locale;
  draft.identity.name = optionalString(basics, "fullName", "$.resume.basics", 200, issues);
  draft.identity.headline = optionalString(basics, "professionalHeadline", "$.resume.basics", 300, issues);
  draft.profile.summary = optionalString(basics, "professionalSummary", "$.resume.basics", 12_000, issues);
  draft.contact.email = optionalString(contact, "email", "$.resume.contact", 320, issues);
  draft.contact.phone = optionalString(contact, "phone", "$.resume.contact", 100, issues);
  draft.contact.location = optionalString(contact, "cityRegion", "$.resume.contact", 300, issues);
  draft.contact.website = optionalString(contact, "website", "$.resume.contact", 2_000, issues);

  draft.experiences = optionalArray(resume, "experience", "$.resume", 100, issues).map((item, index) => {
    const path = `$.resume.experience[${index}]`;
    const record = objectAt(item, path, issues);
    rejectUnknownKeys(record, ["organization", "role", "location", "startDate", "endDate", "current", "summary", "achievements"], path, issues);
    const result = createExperience();
    result.organization = optionalString(record, "organization", path, 500, issues);
    result.role = optionalString(record, "role", path, 500, issues);
    result.location = optionalString(record, "location", path, 300, issues);
    result.startDate = optionalString(record, "startDate", path, 10, issues);
    result.endDate = optionalString(record, "endDate", path, 10, issues);
    result.current = optionalBoolean(record, "current", path, issues);
    if (result.current) result.endDate = "";
    result.summary = optionalString(record, "summary", path, 8_000, issues);
    result.bullets = optionalStringArray(record, "achievements", path, 100, 2_000, issues);
    return result;
  });

  draft.education = optionalArray(resume, "education", "$.resume", 100, issues).map((item, index) => {
    const path = `$.resume.education[${index}]`;
    const record = objectAt(item, path, issues);
    rejectUnknownKeys(record, ["institution", "qualification", "fieldOfStudy", "location", "startDate", "endDate", "current", "details"], path, issues);
    const result = createEducation();
    result.institution = optionalString(record, "institution", path, 500, issues);
    result.qualification = optionalString(record, "qualification", path, 500, issues);
    result.field = optionalString(record, "fieldOfStudy", path, 500, issues);
    result.location = optionalString(record, "location", path, 300, issues);
    result.startDate = optionalString(record, "startDate", path, 10, issues);
    result.endDate = optionalString(record, "endDate", path, 10, issues);
    result.current = optionalBoolean(record, "current", path, issues);
    if (result.current) result.endDate = "";
    result.details = optionalString(record, "details", path, 8_000, issues);
    return result;
  });

  draft.skills = optionalArray(resume, "skills", "$.resume", 300, issues).map((item, index) => {
    const path = `$.resume.skills[${index}]`;
    const record = objectAt(item, path, issues);
    rejectUnknownKeys(record, ["name", "level", "keywords"], path, issues);
    const result = createSkill();
    result.name = optionalString(record, "name", path, 300, issues);
    result.level = optionalString(record, "level", path, 100, issues);
    result.keywords = optionalStringArray(record, "keywords", path, 100, 300, issues);
    return result;
  });

  draft.projects = optionalArray(resume, "projects", "$.resume", 100, issues).map((item, index) => {
    const path = `$.resume.projects[${index}]`;
    const record = objectAt(item, path, issues);
    rejectUnknownKeys(record, ["name", "description", "url", "startDate", "endDate", "outcomes"], path, issues);
    const result = createProject();
    result.name = optionalString(record, "name", path, 500, issues);
    result.description = optionalString(record, "description", path, 8_000, issues);
    result.url = optionalString(record, "url", path, 2_000, issues);
    result.startDate = optionalString(record, "startDate", path, 10, issues);
    result.endDate = optionalString(record, "endDate", path, 10, issues);
    result.bullets = optionalStringArray(record, "outcomes", path, 100, 2_000, issues);
    return result;
  });

  if (!hasImportContent(draft)) {
    issues.push("$.resume is empty. Add at least one resume value before importing.");
  }
  if (issues.length > 0) throw new ResumeImportError(issues.slice(0, 50));

  const warnings = [...new Set(Object.values(validateDraftFormats(draft)))];
  return {
    draft,
    summary: {
      name: draft.identity.name || "Unnamed resume",
      locale: draft.locale,
      experience: draft.experiences.length,
      education: draft.education.length,
      skills: draft.skills.length,
      projects: draft.projects.length,
    },
    warnings,
  };
}

function objectAt(value: unknown, path: string, issues: string[]): Record<string, unknown> {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  issues.push(`${path} must be a JSON object.`);
  return {};
}

function optionalObject(record: Record<string, unknown>, key: string, path: string, issues: string[]): Record<string, unknown> {
  const value = record[key];
  return value === undefined ? {} : objectAt(value, `${path}.${key}`, issues);
}

function optionalString(record: Record<string, unknown>, key: string, path: string, max: number, issues: string[]): string {
  const value = record[key];
  if (value === undefined || value === null) return "";
  if (typeof value !== "string") {
    issues.push(`${path}.${key} must be a string.`);
    return "";
  }
  if (value.length > max) {
    issues.push(`${path}.${key} exceeds the ${max}-character limit.`);
    return "";
  }
  return value.trim();
}

function optionalBoolean(record: Record<string, unknown>, key: string, path: string, issues: string[]): boolean {
  const value = record[key];
  if (value === undefined || value === null) return false;
  if (typeof value !== "boolean") {
    issues.push(`${path}.${key} must be true or false.`);
    return false;
  }
  return value;
}

function optionalArray(record: Record<string, unknown>, key: string, path: string, max: number, issues: string[]): unknown[] {
  const value = record[key];
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    issues.push(`${path}.${key} must be an array.`);
    return [];
  }
  if (value.length > max) {
    issues.push(`${path}.${key} contains more than ${max} entries.`);
    return [];
  }
  return value;
}

function optionalStringArray(
  record: Record<string, unknown>,
  key: string,
  path: string,
  maxItems: number,
  maxLength: number,
  issues: string[],
): string[] {
  const values = optionalArray(record, key, path, maxItems, issues);
  return values.flatMap((value, index) => {
    if (typeof value !== "string") {
      issues.push(`${path}.${key}[${index}] must be a string.`);
      return [];
    }
    if (value.length > maxLength) {
      issues.push(`${path}.${key}[${index}] exceeds the ${maxLength}-character limit.`);
      return [];
    }
    const normalized = value.trim();
    return normalized ? [normalized] : [];
  });
}

function rejectUnknownKeys(record: Record<string, unknown>, allowed: string[], path: string, issues: string[]): void {
  const known = new Set(allowed);
  for (const key of Object.keys(record)) {
    if (!known.has(key)) issues.push(`${path}.${key} is not supported. Remove it or move the value to a documented field.`);
  }
}

function hasImportContent(draft: ResumeDraft): boolean {
  return Boolean(
    draft.identity.name
      || draft.identity.headline
      || draft.profile.summary
      || draft.contact.email
      || draft.contact.phone
      || draft.contact.location
      || draft.contact.website
      || draft.experiences.length
      || draft.education.length
      || draft.skills.length
      || draft.projects.length,
  );
}
